import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '@/components/Spinner';
import { Pager } from '@/components/Pager';
import { SearchBox } from '@/components/form/SearchBox';
import { SortableHeader } from '@/components/SortableHeader';
import { useColumnSort } from '@/lib/tableSort';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useAsyncData } from '@/hooks/useAsyncData';
import { formatDate } from '@/lib/clientFormat';
import { fetchSupportTickets } from '@/api/support';

type SortKey = 'created_at' | 'title';

/**
 * "Обращения в поддержку" — 1:1 старый Orchid SupportListScreen, но в
 * раскладке референса «Компас»: клиент, тема обращения с описанием второй
 * строкой и дата создания. Само обращение (текст целиком и вложения)
 * открывается отдельной страницей — в Orchid его нельзя было прочитать,
 * описание обрезалось прямо в ячейке.
 */
export function SupportPage() {
  const navigate = useNavigate();
  const [search, searchInput, setSearchInput] = useDebouncedSearch();
  const [page, setPage] = useState(1);

  const sort = useColumnSort<SortKey>();
  const sortParam = sort.key ? (sort.dir === 'desc' ? `-${sort.key}` : sort.key) : undefined;

  useEffect(() => {
    setPage(1);
  }, [search]);

  const { data: list, error } = useAsyncData(
    () => fetchSupportTickets({ search: search || undefined, sort: sortParam, page }),
    [search, sortParam, page],
    'Не удалось загрузить обращения.',
  );
  const rows = list?.items ?? null;
  const meta = list?.meta ?? null;

  return (
    <section className="catalog">
      <header className="catalog__header">
        <h1>Обращения в поддержку</h1>
        <p className="catalog__lede">
          Все заявки в техподдержку по клиентам
          {meta ? ` · всего ${meta.total}` : ''}
        </p>
      </header>

      <div className="card">
        <div className="filter-row">
          <SearchBox value={searchInput} onChange={setSearchInput} placeholder="Поиск по клиенту" />
        </div>

        {error && <p className="field-error">{error}</p>}

        {rows === null ? (
          <div className="panel-loading">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <p className="cell-muted">Обращений не найдено.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Клиент</th>
                  <SortableHeader label="Тема обращения" sortKey="title" sort={sort} onSort={sort.toggle} />
                  <SortableHeader label="Дата создания" sortKey="created_at" sort={sort} onSort={sort.toggle} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="table__row--clickable ticket-row"
                    onClick={() => navigate(`/support/${row.id}`)}
                  >
                    <td>
                      <div className="cell-primary">{row.client ?? 'Без клиента'}</div>
                      {row.authorEmail && <div className="cell-sub">{row.authorEmail}</div>}
                    </td>
                    <td>
                      <div className="cell-primary">{row.title ?? 'Без темы'}</div>
                      {row.descriptionPreview && (
                        <div className="cell-sub ticket-desc">{row.descriptionPreview}</div>
                      )}
                    </td>
                    <td className="cell-muted tabular">{formatDate(row.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {meta && meta.lastPage > 1 && (
          <div className="table-footer">
            <span className="table-footer__status">
              Страница {meta.currentPage} из {meta.lastPage} · всего {meta.total}
            </span>
            <Pager page={meta.currentPage} lastPage={meta.lastPage} onPageChange={setPage} />
          </div>
        )}
      </div>
    </section>
  );
}
